import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AdminShell } from '../../components/AdminShell';
import { startActAsShop } from '../../components/AppShell';
import { mrrFor, healthFor } from '../../lib/adminQueries';
import { EarningsCard } from '../../components/EarningsCard';
import { fmtMoney, fmtDateTime } from '../../lib/shopDetail/helpers';
import {
  DetailHead, StatCards, ActivityTimeline,
  AccountInfoPanel, BarbersWithLogins,
} from '../../components/shopDetail/Panels';
import { OwnerPanel } from '../../components/shopDetail/OwnerPanel';
import { PromotionsCard } from '../../components/shopDetail/PromotionsCard';
import { SmsActivityPanel } from '../../components/shopDetail/SmsActivityPanel';
import { DeleteShopModal, DeleteResultModal } from '../../components/shopDetail/DeleteModals';

/**
 * Master-admin Shop Detail page — the "everything about this shop" view.
 *
 * Structure:
 *   • DetailHead + StatCards + payment-mix + EarningsCard (top of page).
 *   • dcols right rail: Activity, Owner, Account, Promotions,
 *     BarbersWithLogins.
 *   • Delete flow: two modals gated by the shop being suspended first.
 *
 * This container owns state, data loading, and callAuth. Every visual
 * block is extracted under `components/shopDetail/`.
 */
const ShopDetailInner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState({ mrr: 0, bookings30: 0, completionRate: 0, lifetimeSpend: 0, cash30: 0, card30: 0, stripe30: 0, outstanding30: 0 });
  const [timeline, setTimeline] = useState([]);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ownerBusy, setOwnerBusy] = useState(false);
  const [ownerMsg, setOwnerMsg] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');

  const [showDelete, setShowDelete] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [linkedCount, setLinkedCount] = useState(0);
  const [barbersWithLogin, setBarbersWithLogin] = useState([]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      // Count owner user_profiles + barbers with logins for this shop
      const [{ count: profileCount }, { count: barberLoginCount }] = await Promise.all([
        supabase.from('user_profiles').select('id', { count: 'exact', head: true }).eq('shop_id', id).eq('role', 'shop_admin'),
        supabase.from('barbers').select('id', { count: 'exact', head: true }).eq('shop_id', id).not('user_id', 'is', null),
      ]);
      setLinkedCount((profileCount || 0) + (barberLoginCount || 0));

      // Populate the "Barbers with logins" panel so master admin can
      // resend credentials one-by-one.
      const { data: bwl } = await supabase
        .from('barbers')
        .select('id,name,user_id')
        .eq('shop_id', id)
        .not('user_id', 'is', null)
        .order('name');
      setBarbersWithLogin(bwl || []);
    })();
  }, [id]);

  const refreshOwner = async (shopId) => {
    // First-created shop_admin for this shop is treated as "the owner"
    const { data: ownerRow } = await supabase
      .from('user_profiles')
      .select('id,full_name,phone,shop_id,role')
      .eq('shop_id', shopId)
      .eq('role', 'shop_admin')
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (!ownerRow) return setOwner(null);

    // Fetch owner email via the backend (auth.users is not accessible
    // through PostgREST). Fails gracefully — name/phone still show.
    let email = null;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/owner-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ user_id: ownerRow.id }),
      });
      if (res.ok) {
        const json = await res.json();
        email = json.email || null;
      }
    } catch (e) { console.debug('[shop-detail] non-fatal load error:', e); }

    setOwner({ id: ownerRow.id, name: ownerRow.full_name || '—', phone: ownerRow.phone || null, email });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [{ data: sh }, { data: s }] = await Promise.all([
        supabase.from('shops').select('*').eq('id', id).maybeSingle(),
        supabase.from('platform_settings').select('*').maybeSingle(),
      ]);
      setShop(sh);
      setSettings(s);
      if (!sh) { setLoading(false); return; }

      const day = 86400000;
      const now = new Date();
      const thirty = new Date(now.getTime() - 30 * day).toISOString();

      const { data: bks } = await supabase.from('bookings')
        .select('id,starts_at,status,deposit_amount,deposit_paid,paid_in_full,balance_paid_method,balance_paid_amount,service:services(name,price),customer:customers(name),barber:barbers(name),created_at,channel')
        .eq('shop_id', id)
        .gte('starts_at', thirty);

      const total30 = (bks || []).filter((b) => b.status !== 'cancelled').length;
      const completed = (bks || []).filter((b) => b.status === 'completed');
      const finished  = (bks || []).filter((b) => b.status === 'completed' || b.status === 'no_show');
      const completionRate = finished.length ? (completed.length / finished.length) * 100 : 0;
      const spend = completed.reduce((a, b) => a + Number(b.service?.price || 0), 0);

      // Audit rollups — 30-day cash-vs-card split from balance_paid_*
      // + a pure Stripe/prepaid revenue slice so the master admin can
      // eyeball each shop's mix.
      let cash30 = 0, card30 = 0, stripe30 = 0, outstanding30 = 0;
      (bks || []).forEach((b) => {
        if (b.balance_paid_method === 'cash') cash30 += Number(b.balance_paid_amount ?? 0);
        if (b.balance_paid_method === 'card') card30 += Number(b.balance_paid_amount ?? 0);
        // Stripe = paid via the customer-facing wizard (paid_in_full=true
        // AND no at-chair collection recorded)
        if (b.paid_in_full && !b.balance_paid_method) {
          stripe30 += Number(b.service?.price ?? 0);
        } else if (b.deposit_paid && !b.paid_in_full) {
          stripe30 += Number(b.deposit_amount ?? 0);
        }
        // Outstanding = live bookings still owing (future or unpaid past)
        if (b.status !== 'cancelled' && !b.paid_in_full) {
          const price = Number(b.service?.price ?? 0);
          const dep = Number(b.deposit_amount ?? 0);
          outstanding30 += Math.max(0, price - (b.deposit_paid ? dep : 0));
        }
      });

      setStats({
        mrr: mrrFor(sh, s),
        bookings30: total30,
        completionRate,
        lifetimeSpend: spend,
        cash30, card30, stripe30, outstanding30,
      });

      // Activity timeline — derive from real events. Last 12 items.
      const events = [];
      events.push({ at: sh.created_at, kind: 'shop_created', text: 'Shop created', tone: 'b' });
      const { data: barbers } = await supabase.from('barbers').select('name,created_at').eq('shop_id', id).order('created_at', { ascending: false }).limit(5);
      (barbers || []).forEach((b) => events.push({ at: b.created_at, kind: 'barber_added', text: `Added barber ${b.name}`, tone: 'b' }));
      const { data: svcs } = await supabase.from('services').select('name,created_at').eq('shop_id', id).order('created_at', { ascending: false }).limit(5);
      (svcs || []).forEach((sv) => events.push({ at: sv.created_at, kind: 'service_added', text: `Added service ${sv.name}`, tone: 'b' }));
      (bks || []).slice(0, 10).forEach((b) => {
        events.push({
          at: b.created_at || b.starts_at,
          kind: 'booking',
          text: `${b.status === 'no_show' ? 'No-show' : 'Booking'} · ${b.customer?.name || '—'} · ${b.service?.name || '—'}`,
          tone: b.status === 'no_show' ? 'a' : 'g',
        });
      });
      events.sort((a, b) => new Date(b.at) - new Date(a.at));
      setTimeline(events.slice(0, 12));

      await refreshOwner(id);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <main className="main admin-page">
        <div className="topbar"><div><h2>Shop</h2></div></div>
        <div className="body"><div style={{ padding: 40, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div></div>
      </main>
    );
  }
  if (!shop) {
    return (
      <main className="main admin-page">
        <div className="topbar"><div><h2>Shop not found</h2></div></div>
      </main>
    );
  }

  const health = healthFor({ subscriptionStatus: shop.subscription_status, lastLoginAt: null, bookingsThisWeek: 0, bookingsLastWeek: 0 });

  const callAuth = async (path, body) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let json = {};
    try { json = JSON.parse(text); } catch (_e) { /* keep raw */ }
    if (!res.ok) throw new Error(json.detail || text || `HTTP ${res.status}`);
    return json;
  };

  const handleSuspend = async () => {
    if (!window.confirm(
      `Suspend ${shop.name}? Their Stripe subscription will be cancelled IMMEDIATELY ` +
      `(no proration credit) and they'll lose access at once.`
    )) return;
    try {
      const res = await callAuth('/api/auth/suspend-shop', { shop_id: shop.id });
      setShop({ ...shop, subscription_status: 'cancelled', cancel_at_period_end: false });
      if (res.stripe_error) {
        alert(`Shop marked as suspended, but Stripe cancel returned: ${res.stripe_error}`);
      }
    } catch (e) {
      alert(`Suspend failed: ${e.message}`);
    }
  };

  // Undo a suspend by creating a fresh Stripe subscription against the
  // customer's saved default card. If the shop has no card on file,
  // the backend returns a 400 telling us to send the owner through
  // Billing to add one first.
  const handleResume = async () => {
    if (!window.confirm(
      `Resume ${shop.name}? A new Stripe subscription will be created on their card ` +
      `and billing will start again from now.`
    )) return;
    try {
      const res = await callAuth('/api/auth/resume-shop', { shop_id: shop.id });
      setShop({
        ...shop,
        subscription_status: res.subscription_status || 'active',
        stripe_subscription_id: res.subscription_id,
        cancel_at_period_end: false,
      });
      alert(`Resumed. New subscription: ${res.subscription_id} (${res.subscription_status}).`);
    } catch (e) {
      alert(`Resume failed: ${e.message}`);
    }
  };

  const inviteOwner = async () => {
    setOwnerBusy(true); setOwnerMsg('');
    try {
      await callAuth('/api/auth/create-shop-admin', {
        email: inviteEmail.trim().toLowerCase(),
        shop_id: shop.id,
        name: inviteName.trim(),
      });
      setOwnerMsg(`Owner account created — reset link emailed to ${inviteEmail}.`);
      setShowInvite(false);
      setInviteEmail(''); setInviteName('');
      await refreshOwner(shop.id);
    } catch (e) {
      setOwnerMsg(`Failed: ${e.message}`);
    } finally { setOwnerBusy(false); }
  };

  const sendOwnerReset = async () => {
    const email = window.prompt("Owner's email address? (needed once — we don't hold it in the database)");
    if (!email) return;
    setOwnerBusy(true); setOwnerMsg('');
    try {
      await callAuth('/api/auth/send-reset', { email: email.trim().toLowerCase() });
      setOwnerMsg(`Reset link sent to ${email}.`);
    } catch (e) {
      setOwnerMsg(`Failed: ${e.message}`);
    } finally { setOwnerBusy(false); }
  };

  // Resend a fresh temp password to the owner. Invalidates their current
  // one; they'll be forced to change it on first login (must_change_password
  // is re-set on the backend).
  const resendOwnerCredentials = async () => {
    if (!owner) return;
    if (!window.confirm(
      `Resend a new temporary password to the owner? Their CURRENT password will stop working immediately.`
    )) return;
    setOwnerBusy(true); setOwnerMsg('');
    try {
      await callAuth('/api/auth/resend-owner-credentials', { user_id: owner.id });
      setOwnerMsg(`New credentials emailed to the owner.`);
    } catch (e) {
      setOwnerMsg(`Failed: ${e.message}`);
    } finally { setOwnerBusy(false); }
  };

  // Resend credentials for one barber (identified by their user_id).
  const resendBarberCredentials = async (userId, barberName) => {
    if (!userId) return;
    if (!window.confirm(
      `Resend a new temporary password to ${barberName}? Their CURRENT password will stop working immediately.`
    )) return;
    try {
      await callAuth('/api/auth/resend-barber-credentials', { user_id: userId });
      alert(`New credentials emailed to ${barberName}.`);
    } catch (e) {
      alert(`Failed: ${e.message}`);
    }
  };

  const isSuspended = shop.subscription_status === 'cancelled';

  return (
    <main className="main admin-page">
      <div className="topbar">
        <div className="bc">
          <Link to="/admin/shops">Shops</Link>
          <span className="sep">/</span>
          <span className="cur">{shop.name}</span>
        </div>
      </div>

      <div className="body">
        <DetailHead
          shop={shop}
          isSuspended={isSuspended}
          onActAs={() => { startActAsShop(shop.id); navigate('/dashboard'); }}
          onSuspend={handleSuspend}
          onResume={handleResume}
          onDelete={() => setShowDelete(true)}
        />

        <StatCards stats={stats} fmtMoney={fmtMoney} />

        {/* Earnings graph — master admin sees the full week→year
            filter regardless of the shop's plan. */}
        <EarningsCard
          shopId={id}
          plan="premium"
          title={`${shop.name} · earnings`}
          subtitle="Full payments only — deposits excluded"
          defaultPeriod="month"
          dataTestId="admin-shop-earnings"
        />

        <div className="dcols" style={{ marginTop: 14 }}>
          <ActivityTimeline timeline={timeline} fmtDateTime={fmtDateTime} />

          <SmsActivityPanel shopId={id} />

          <OwnerPanel
            owner={owner}
            ownerBusy={ownerBusy}
            ownerMsg={ownerMsg}
            showInvite={showInvite}
            setShowInvite={setShowInvite}
            inviteEmail={inviteEmail}
            setInviteEmail={setInviteEmail}
            inviteName={inviteName}
            setInviteName={setInviteName}
            onInvite={inviteOwner}
            onSendReset={sendOwnerReset}
            onResendCredentials={resendOwnerCredentials}
          />

          <AccountInfoPanel shop={shop} health={health} />

          <PromotionsCard shopId={shop.id} subscriptionStatus={shop.subscription_status} />

          <BarbersWithLogins
            barbersWithLogin={barbersWithLogin}
            onResend={resendBarberCredentials}
          />
        </div>
      </div>

      {showDelete && (
        <DeleteShopModal
          shop={shop}
          linkedAccountsHint={linkedCount}
          onClose={() => setShowDelete(false)}
          onDeleted={(result) => { setDeleteResult(result); setShowDelete(false); }}
          callAuth={callAuth}
        />
      )}
      {deleteResult && (
        <DeleteResultModal
          result={deleteResult}
          onClose={() => window.location.assign('/admin/shops')}
        />
      )}
    </main>
  );
};

const ShopDetail = () => <AdminShell><ShopDetailInner /></AdminShell>;
export default ShopDetail;
